import { SpotStatus } from '@nova/core';

import { useYardSpotStatus } from '@satellite/composables/useYardSpotStatus';
import { getInboundInProgressMoveTask } from '@satellite/composables/yard/spotGridTypes';
import { useYardInTransitFeature } from '@satellite/composables/yard/useYardInTransitFeature';
import {
  getSpotStatusBackgroundColor,
  getSpotStatusLabelKey
} from '@satellite/helpers/yardTaskUtils';

import type { SpotGridItem } from '@satellite/composables/useYardSpotGridPresenter';

/**
 * The status a spot picker shows, which is not always the status the spot holds: a move that
 * reserves its destination keeps that reserve for the whole trip, so a spot with an asset on its
 * way to it stays `reserved_automated` and only its in-progress inbound move says otherwise. The
 * spot card already reads it that way; this is the same rule for the pickers, and for the chip
 * colour that follows the status. Only a reserved spot is overridden — closed and occupied both
 * say something an inbound move does not replace.
 *
 * Gates itself on `enable-yard-in-transit`, so with the flag off every caller sees the raw status.
 */
export function useYardSpotDisplayStatus() {
  const { isYardInTransitVisible } = useYardInTransitFeature();
  const spotStatus = useYardSpotStatus();

  const getSpotDisplayStatus = (spot: SpotGridItem): SpotStatus => {
    if (
      isYardInTransitVisible.value &&
      spotStatus.isSpotReserved(spot) &&
      getInboundInProgressMoveTask(spot)
    ) {
      return SpotStatus.IN_TRANSIT;
    }

    return spot.status;
  };

  const getSpotDisplayStatusKey = (spot: SpotGridItem): string =>
    getSpotStatusLabelKey(getSpotDisplayStatus(spot));

  const getSpotDisplayStatusColor = (spot: SpotGridItem): string =>
    getSpotStatusBackgroundColor(getSpotDisplayStatus(spot));

  return {
    getSpotDisplayStatus,
    getSpotDisplayStatusKey,
    getSpotDisplayStatusColor
  };
}
